import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import { FREE } from 'self-service-api/constants/ProductLevels';
import { SuiteCTA } from '../../../features/purchaseMotions/components/SuiteCTA';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useSuitePurchaseMotionsQuery } from '../../../features/purchaseMotions/hooks/useSuitePurchaseMotionsQuery';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { useAppSelector } from '../../../reducers';
import { currentSuiteSkuIdsForProductLevelSelector, getTBSuiteProductForProductLevelSelector } from '../../../selectors/ProductsSelectors';
import { ErrorBoundary } from '../../../v2/components/error-boundary/ErrorBoundary';
import { ErrorBoundaryTrigger } from '../../../v2/components/error-boundary/ErrorBoundaryTrigger';
import { SUITE_FEATURE_TABLE_HEADER } from '../../../v2/constants/tracking/TrackingSources';
import { useHasApiNameInProductInventory } from '../../../v2/hooks/inventory/useProductInventoryLookup';
import CodeSplitEnvWrapper from '../../common/env/CodeSplitEnvWrapper';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import FeatureTableColTag from '../FeatureTableColTag';
import FeatureTableFreePriceDisplay from '../FeatureTableFreePriceDisplay';
import FeatureTablePriceDisplay from '../FeatureTablePriceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SuiteFeatureTableHeaderCell = ({
  productLevel,
  screen,
  badgeName
}) => {
  const isAppReady = useGlobalLoading();
  const isFree = productLevel === FREE;
  const suiteProduct = useAppSelector(state => {
    if (!isAppReady || isFree) return null;
    return getTBSuiteProductForProductLevelSelector(state, {
      suiteProductLevel: productLevel
    });
  });
  const apiName = isFree ? 'hubspot-free' : suiteProduct === null || suiteProduct === void 0 ? void 0 : suiteProduct.apiName;
  const {
    hasProductInInventory: isCurrentPlanLevel,
    loading: isCurrentPlanLevelLoading
  } = useHasApiNameInProductInventory({
    apiName
  });
  const {
    suitePurchaseMotions,
    error: suitePurchaseMotionsError,
    loading: suitePurchaseMotionsLoading
  } = useSuitePurchaseMotionsQuery({
    productLevel,
    location: 'MERCH_CARD'
  });
  const suitePurchaseMotion = suitePurchaseMotions.length > 0 ? suitePurchaseMotions[0] : null;
  const ctaVariantProps = getButtonVariantProps({
    variant: 'table-header'
  });
  const trellisCtaVariantProps = getTrellisButtonVariantProps({
    variant: 'table-header',
    visible: isCurrentPlanLevel && !isCurrentPlanLevelLoading
  });
  const skuIds = useAppSelector(state => {
    if (!isAppReady) return [];
    if (productLevel === FREE) return [];
    return currentSuiteSkuIdsForProductLevelSelector(state, {
      productLevel
    });
  });
  const suiteSkuId = (skuIds === null || skuIds === void 0 ? void 0 : skuIds.length) === 1 ? skuIds[0] : null;
  const TitleText = () => {
    if (screen !== 'crm') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `compare.productLevel.${productLevel}`
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `products.crm.${productLevel}`
    });
  };
  const isLoading = !isAppReady || isCurrentPlanLevelLoading || suitePurchaseMotionsLoading;
  if (isLoading || !apiName) return null;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
      apiName: apiName,
      badgeName: badgeName
    }), /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(TitleText, {})
    }), /*#__PURE__*/_jsx(View, {
      paddingBlock: "100",
      children: isFree ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoading,
        heights: [20],
        widths: [140],
        children: /*#__PURE__*/_jsx(FeatureTableFreePriceDisplay, {})
      }) : /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [20],
        widths: [140],
        children: /*#__PURE__*/_jsx(FeatureTablePriceDisplay, {
          apiName: apiName
        })
      })
    }), isFree ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !isAppReady,
      heights: [40],
      widths: [140],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          authedComponentLoader: () => import( /* webpackChunkName: "authed-chunk" */`pricing-pages-ui/components/CurrentPlanButton`),
          publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/common/SignupButton`),
          authedComponentProps: Object.assign({
            visible: true
          }, ctaVariantProps),
          publicComponentProps: Object.assign({
            sourceId: SUITE_FEATURE_TABLE_HEADER,
            productLine: 'crm',
            use: 'primary'
          }, ctaVariantProps, {
            trackingSource: SUITE_FEATURE_TABLE_HEADER
          })
        })
      }))
    }) : /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !isAppReady,
      heights: [40],
      widths: [140],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsxs(ErrorBoundary, {
          fallback: suiteSkuId ? /*#__PURE__*/_jsx(TalkToSalesFallback, {
            skuId: suiteSkuId,
            trackingSource: SUITE_FEATURE_TABLE_HEADER,
            screen: screen
          }) : null,
          children: [/*#__PURE__*/_jsx(ErrorBoundaryTrigger, {
            error: suitePurchaseMotionsError
          }), suitePurchaseMotion && /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
              purchaseMotion: suitePurchaseMotion,
              productLevel: productLevel,
              trackingSource: SUITE_FEATURE_TABLE_HEADER,
              screen: screen,
              purchaseMotionLocation: 'GENERIC' // replace with FEATURE_TABLE when available https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/11974
            }, ctaVariantProps))
          }, String(suitePurchaseMotion))]
        })
      }))
    })]
  });
};
export default SuiteFeatureTableHeaderCell;